import type { BillingStrategy } from '@ctct/domain-layer';
import type { PaymentButtonPageStore } from '@ctct/state-client-layer';
import type { UseBillingInfo } from '@ctct/state-server-layer';

import type { TranslationPaths } from '../../../types';

export interface PaymentTermsContentKeys {
  autoRenewalTermsKey: TranslationPaths | null;
  chargesTermsKey: TranslationPaths | null;
  termsAcknowledgementKey: TranslationPaths;
  frequencyKey?: TranslationPaths;
  termsCheckboxA11yTitleKey: TranslationPaths;
}

export interface PaymentTermsI18nKeys {
  monthlyTerms: TranslationPaths;
  monthlyTermsSocialStarter: TranslationPaths;
  multimonthTerms: TranslationPaths;
  multimonthChargeProcess: TranslationPaths;
  every6: TranslationPaths;
  every12: TranslationPaths;
  termsAcknowledgement: TranslationPaths;
  termsCheckboxA11yTitle: TranslationPaths;
}

type CountryGroup = 'core-country' | 'non-core-country';
type CustomerType = 'trialer' | 'customer';

export function createPaymentTermsI18nKeys({
  billingStrategy,
  countryGroup,
  customerType,
}: {
  billingStrategy: BillingStrategy;
  countryGroup: CountryGroup;
  customerType: CustomerType;
}): PaymentTermsI18nKeys {
  const base = `application-layer.auto-renewal.${billingStrategy}` as const;
  const suffix = `${countryGroup}.${customerType}` as const;

  return {
    monthlyTerms: `${base}.monthly-terms.${suffix}`,
    monthlyTermsSocialStarter: 'application-layer.monthly-terms-social-starter',
    multimonthTerms: `${base}.multimonth-terms.${suffix}`,
    multimonthChargeProcess: `${base}.multimonth-charge-process.${suffix}`,
    every6: `${base}.every-6`,
    every12: `${base}.every-12`,
    termsAcknowledgement: `${base}.terms-acknowledgement.${suffix}`,
    termsCheckboxA11yTitle: `${base}.terms-checkbox-a11y-title`,
  };
}

export class TranslationsService {
  constructor(
    private billingStrategy: BillingStrategy,
    private pageStore: PaymentButtonPageStore,
    private billingInfoQuery: UseBillingInfo
  ) {
    this.initializeBillingCountryCode();
  }

  api() {
    return {};
  }

  data() {
    return {
      getPaymentTermsContentKeys: (
        paymentPeriod: string,
        isSocialStarter: boolean,
        billingCountryCode?: string | null,
        isCustomer?: boolean
      ): PaymentTermsContentKeys => {
        const translationKeys = createPaymentTermsI18nKeys({
          billingStrategy: this.billingStrategy,
          countryGroup: this.getCountryGroup(billingCountryCode),
          customerType: isCustomer ? ('customer' as CustomerType) : ('trialer' as CustomerType),
        });
        const termsCheckboxA11yTitleKey = translationKeys.termsCheckboxA11yTitle;

        if (isSocialStarter) {
          return {
            autoRenewalTermsKey: translationKeys.monthlyTermsSocialStarter,
            chargesTermsKey: null,
            termsAcknowledgementKey: translationKeys.termsAcknowledgement,
            termsCheckboxA11yTitleKey,
          };
        }

        switch (paymentPeriod) {
          case '0':
          case '1': {
            return {
              autoRenewalTermsKey: translationKeys.monthlyTerms,
              chargesTermsKey: null,
              termsAcknowledgementKey: translationKeys.termsAcknowledgement,
              termsCheckboxA11yTitleKey,
            };
          }
          case '6':
          case '12': {
            const frequencyKey = paymentPeriod === '6' ? translationKeys.every6 : translationKeys.every12;

            return {
              autoRenewalTermsKey: translationKeys.multimonthTerms,
              chargesTermsKey: translationKeys.multimonthChargeProcess,
              termsAcknowledgementKey: translationKeys.termsAcknowledgement,
              frequencyKey,
              termsCheckboxA11yTitleKey,
            };
          }
          default:
            return {
              autoRenewalTermsKey: null,
              chargesTermsKey: null,
              termsAcknowledgementKey: translationKeys.termsAcknowledgement,
              termsCheckboxA11yTitleKey,
            };
        }
      },
    };
  }

  handlers() {
    return {};
  }

  private initializeBillingCountryCode() {
    const currentCountryCode = this.pageStore.getState().paymentButtonBillingCountryCode;

    if (currentCountryCode != null) {
      return;
    }

    const billingCountryCode = this.billingInfoQuery.data?.getCountry()?.trim() ?? null;

    if (billingCountryCode) {
      this.pageStore.setState({ paymentButtonBillingCountryCode: billingCountryCode });
    }
  }

  private getCountryGroup = (billingCountryCode?: string | null): CountryGroup => {
    if (!billingCountryCode) {
      return 'core-country' as CountryGroup;
    }

    const coreCountries = ['US', 'CA', 'UK', 'GB', 'NZ', 'AU', 'IE', 'IN'];
    const upperCased = billingCountryCode.toUpperCase();

    return coreCountries.includes(upperCased) ? ('core-country' as CountryGroup) : ('non-core-country' as CountryGroup);
  };
}
